import { useEffect, Suspense } from "react";
import { lazyWithReload as lazy } from "./lib/lazyWithReload";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CasaneoAuthProvider, useCasaneoAuth } from "./context/CasaneoAuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ViewRoleProvider, useViewRole } from "./context/ViewRoleContext";
import { Spinner } from "./components/Shared";
const Layout = lazy(() => import("./components/Layout"));
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import CasaneoLogin from "./pages/CasaneoLogin";
import CasaneoAuthCallback from "./pages/CasaneoAuthCallback";
const CasaneoRegister = lazy(() => import("./pages/CasaneoRegister"));
const AppSignupLanding = lazy(() => import("./pages/AppSignupLanding"));
const SeoGuide = lazy(() => import("./pages/SeoGuide"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const StatusPage = lazy(() => import("./pages/StatusPage"));
const CasaneoInviteAccept = lazy(() => import("./pages/CasaneoInviteAccept"));
const OwnerReview = lazy(() => import("./pages/public/OwnerReview"));
const TaxRegimeChoice = lazy(() => import("./pages/public/TaxRegimeChoice"));
const QuoteAccept = lazy(() => import("./pages/public/QuoteAccept"));
const OfferAccept = lazy(() => import("./pages/public/OfferAccept"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Reservations = lazy(() => import("./pages/Reservations"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const Properties = lazy(() => import("./pages/Properties"));
const Owners = lazy(() => import("./pages/Owners"));
const Accounting = lazy(() => import("./pages/Accounting"));
const FinanceFix = lazy(() => import("./pages/FinanceFix"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Planning = lazy(() => import("./pages/Planning"));
const Damages = lazy(() => import("./pages/Damages"));
const OwnerProspecting = lazy(() => import("./pages/OwnerProspecting"));
const DataImport = lazy(() => import("./pages/DataImport"));
const FoundersProgram = lazy(() => import("./pages/FoundersProgram"));
const FounderSurvey = lazy(() => import("./pages/FounderSurvey"));
const JustPrice = lazy(() => import("./pages/JustPrice"));
const FeaturePage = lazy(() => import("./pages/FeaturePage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const SeoLandingPage = lazy(() => import("./pages/SeoLandingPage"));
const OwnerEstimate = lazy(() => import("./pages/OwnerEstimate"));
const Simulator = lazy(() => import("./pages/Simulator"));
const SharedSimulation = lazy(() => import("./pages/SharedSimulation"));
const OwnerHome = lazy(() => import("./pages/OwnerHome"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Settings = lazy(() => import("./pages/Settings"));
const BusinessCard = lazy(() => import("./pages/BusinessCard"));
const Lancement = lazy(() => import("./pages/launch/Lancement"));
const SalesKit = lazy(() => import("./pages/SalesKit"));
const DemoKit = lazy(() => import("./pages/DemoKit"));
const GuestForm = lazy(() => import("./pages/public/GuestForm"));
const ContractorPortal = lazy(() => import("./pages/public/ContractorPortal"));
const DemoBooking = lazy(() => import("./pages/public/DemoBooking"));
const WelcomeBookPublic = lazy(() => import("./pages/public/WelcomeBookPublic"));
const SupplementsPage = lazy(() => import("./pages/SupplementsPage"));
const CautionsPage = lazy(() => import("./pages/CautionsPage"));
const Compliance = lazy(() => import("./pages/Compliance"));
const QuotesPendingPage = lazy(() => import("./pages/QuotesPendingPage"));
const FormerProperties = lazy(() => import("./pages/FormerProperties"));
const GuidesPage = lazy(() => import("./pages/GuidesPage"));
const OwnerInvoicesPage = lazy(() => import("./pages/OwnerInvoicesPage"));
import LegalPage from "./pages/LegalPage";
const Team = lazy(() => import("./pages/Team"));
const Reviews = lazy(() => import("./pages/Reviews"));
const FeaturesIndex = lazy(() => import("./pages/FeaturesIndex"));
const AuthorPage = lazy(() => import("./pages/AuthorPage"));
const MessageTemplates = lazy(() => import("./pages/MessageTemplates"));
const PaymentReturn = lazy(() => import("./pages/PaymentReturn"));
const PayPalReturn = lazy(() => import("./pages/PayPalReturn"));
const Impayes = lazy(() => import("./pages/Impayes"));
const TouristTax = lazy(() => import("./pages/TouristTax"));
const TvBoard = lazy(() => import("./pages/TvBoard"));
const PropertyStats = lazy(() => import("./pages/PropertyStats"));
const Voyageurs = lazy(() => import("./pages/Voyageurs"));
const AuditOverbookings = lazy(() => import("./pages/AuditOverbookings"));
const Presentation = lazy(() => import("./pages/Presentation"));
const PricingPage = lazy(() => import("./pages/landing/PricingPage"));
const OwnerDirectPage = lazy(() => import("./pages/landing/OwnerDirectPage"));
const Demo = lazy(() => import("./pages/Demo"));
const DemoLive = lazy(() => import("./pages/DemoLive"));
import Landing from "./pages/landing/Landing";
const AgencySite = lazy(() => import("./pages/public/AgencySite"));
import Reset from "./pages/Reset";
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const StartGuide = lazy(() => import("./pages/StartGuide"));
const Aide = lazy(() => import("./pages/Aide"));
const CleanerHome = lazy(() => import("./pages/CleanerHome"));
import { AppErrorBoundary } from "./components/AppErrorBoundary";
const Onboarding = lazy(() => import("./pages/Onboarding"));
const AirbnbReturn = lazy(() => import("./pages/AirbnbReturn"));
const AirbnbReconnect = lazy(() => import("./pages/AirbnbReconnect"));
const PublicSite = lazy(() => import("./pages/public/PublicSite").then((m) => ({ default: m.PublicSite })));
const PublicProperty = lazy(() => import("./pages/public/PublicSite").then((m) => ({ default: m.PublicProperty })));
const BookingSuccess = lazy(() => import("./pages/public/PublicSite").then((m) => ({ default: m.BookingSuccess })));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-background"><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

// Same auth guard but WITHOUT the Layout chrome — used for full-screen views
// (wall displays, kiosks, …).
function ProtectedRaw({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-background"><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Routes that must be inaccessible in owner view (messaging, planning, accounting, ...).
// The user is still authenticated but is redirected to their owner home.
function OwnerPermitted({ perm, children }) {
  const { isOwnerView, ownerPermissions } = useViewRole();
  if (isOwnerView && ownerPermissions && ownerPermissions[perm] === false) return <Navigate to="/" replace />;
  return children;
}

// Routes interdites à un agent ménage (fiches logements, espace propriétaire…).
function CleanerBlocked({ children }) {
  const { isCleaner } = useViewRole();
  if (isCleaner) return <Navigate to="/" replace />;
  return children;
}

function OwnerBlocked({ children }) {
  const { isOwnerView, resolving } = useViewRole();
  if (resolving) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-brass" /></div>;
  if (isOwnerView) return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-background"><Spinner /></div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// Redirects the root URL depending on the current view mode.
function Home() {
  const { isOwnerView, isCleaner, resolving } = useViewRole();
  if (isCleaner) return <CleanerHome />;
  // Tant que le backend n'a pas confirmé le rôle, pas d'espace propriétaire « flash » pour un gestionnaire.
  if (resolving) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-brass" /></div>;
  return isOwnerView ? <OwnerHome /> : <Dashboard />;
}

// Racine : landing publique pour les visiteurs, tableau de bord pour les connectés.
function Root() {
  const { user, loading, adoptCasaneoUser } = useAuth();
  const { casaneoUser } = useCasaneoAuth();
  useEffect(() => { if (!loading && !user && casaneoUser) adoptCasaneoUser(casaneoUser); }, [loading, user, casaneoUser, adoptCasaneoUser]);
  if (loading || (!user && casaneoUser)) return <div className="min-h-screen grid place-items-center bg-[#071e42]"><Spinner /></div>;
  if (!user) return <Landing />;
  return <Layout><Home /></Layout>;
}

function AppRouter() {
  const location = useLocation();
  // Casanéo Google OAuth landing — dedicated path so we don't clash with the
  // legacy external OAuth callback (which also uses `#session_id=`).
  if (location.pathname === "/casaneo-auth-callback") return <CasaneoAuthCallback />;
  // Détection synchrone du session_id (retour OAuth) AVANT toute route protégée.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Suspense fallback={<div className="min-h-screen grid place-items-center bg-background"><Spinner /></div>}>
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/presentation" element={<Presentation />} />
      <Route path="/fonctionnalites" element={<FeaturesIndex />} />
      <Route path="/fonctionnalites/:slug" element={<FeaturePage />} />
      <Route path="/casaneo-vs-guesty" element={<ComparePage />} />
      <Route path="/casaneo-vs-smoobu" element={<ComparePage />} />
      <Route path="/casaneo-vs-lodgify" element={<ComparePage />} />
      <Route path="/casaneo-vs-beds24" element={<ComparePage />} />
      <Route path="/casaneo-vs-hostaway" element={<ComparePage />} />
      <Route path="/casaneo-vs-superhote" element={<ComparePage />} />
      {["logiciel-conciergerie-airbnb", "logiciel-location-courte-duree", "logiciel-gestion-location-saisonniere", "application-menage-conciergerie", "planning-menage-airbnb", "gestion-degats-location-saisonniere"].map((s) => <Route key={s} path={`/${s}`} element={<SeoLandingPage />} />)}
      <Route path="/tarifs" element={<PricingPage />} />
      <Route path="/proprietaire-direct" element={<OwnerDirectPage />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/programme-fondateurs" element={<FoundersProgram />} />
      <Route path="/enquete/:token" element={<FounderSurvey />} />
      <Route path="/demo/live" element={<DemoLive />} />
      <Route path="/demo/live/:role" element={<DemoLive />} />
      <Route path="/lancement" element={<Lancement />} />
      <Route path="/kit-commercial" element={<ProtectedRaw><SalesKit /></ProtectedRaw>} />
      <Route path="/kit-demo" element={<ProtectedRaw><DemoKit /></ProtectedRaw>} />
      <Route path="/v/:token" element={<GuestForm />} />
      <Route path="/rdv/:token" element={<DemoBooking />} />
      <Route path="/livret/:code" element={<WelcomeBookPublic />} />
      <Route path="/demarrer" element={<Navigate to="/" replace />} />
      <Route path="/agence/:slug/*" element={<AgencySite />} />
      <Route path="/reset" element={<Reset />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reinitialiser-mot-de-passe/:token" element={<ResetPassword />} />
      <Route path="/casaneo-login" element={<CasaneoLogin />} />
      <Route path="/casaneo-register" element={<CasaneoRegister />} />
      <Route path="/inscription-app" element={<AppSignupLanding />} />
      <Route path="/guide/:slug" element={<SeoGuide />} />
      <Route path="/auteur/:slug" element={<AuthorPage />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/statut" element={<StatusPage />} />
      <Route path="/casaneo-invite/:code" element={<CasaneoInviteAccept />} />
      <Route path="/avis-proprietaire/:token" element={<OwnerReview />} />
      <Route path="/regime-fiscal/:token" element={<TaxRegimeChoice />} />
      <Route path="/devis/:token" element={<QuoteAccept />} />
      <Route path="/intervention/:token" element={<ContractorPortal />} />
      <Route path="/offre/:token" element={<OfferAccept />} />
      <Route path="/invitation" element={<AcceptInvite />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      {/* Site public / réservation directe */}
      <Route path="/site/:slug" element={<PublicSite />} />
      <Route path="/site/:slug/logement/:propertyId" element={<PublicProperty />} />
      <Route path="/site/:slug/reservation/:reservationId/succes" element={<BookingSuccess />} />
      <Route path="/site/:slug/succes" element={<BookingSuccess />} />
      <Route path="/paiement/carte-enregistree" element={<PaymentReturn kind="card" />} />
      <Route path="/paiement/succes" element={<PaymentReturn kind="success" />} />
      <Route path="/paiement/annule" element={<PaymentReturn kind="cancel" />} />
      <Route path="/paiement/paypal/retour" element={<PayPalReturn />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/" element={<Root />} />
      <Route path="/mon-espace" element={<Protected><CleanerBlocked><OwnerHome /></CleanerBlocked></Protected>} />
      <Route path="/reservations" element={<Protected><OwnerBlocked><Reservations /></OwnerBlocked></Protected>} />
      <Route path="/voyageurs" element={<Protected><OwnerBlocked><Voyageurs /></OwnerBlocked></Protected>} />
      <Route path="/calendrier" element={<Protected><OwnerPermitted perm="calendar"><CalendarPage /></OwnerPermitted></Protected>} />
      <Route path="/proprietes" element={<Protected><CleanerBlocked><Properties /></CleanerBlocked></Protected>} />
      <Route path="/logements" element={<Navigate to="/proprietes" replace />} />
      <Route path="/airbnb/retour" element={<Protected><CleanerBlocked><AirbnbReturn /></CleanerBlocked></Protected>} />
      <Route path="/airbnb/reconnecter/:propertyId" element={<Protected><CleanerBlocked><AirbnbReconnect /></CleanerBlocked></Protected>} />
      <Route path="/proprietes/:id/stats" element={<Protected><CleanerBlocked><OwnerPermitted perm="stats"><PropertyStats /></OwnerPermitted></CleanerBlocked></Protected>} />
      <Route path="/proprietaires" element={<Protected><OwnerBlocked><Owners /></OwnerBlocked></Protected>} />
      <Route path="/comptabilite" element={<Protected><OwnerBlocked><Accounting /></OwnerBlocked></Protected>} />
      <Route path="/taxe-sejour" element={<Protected><OwnerBlocked><TouristTax /></OwnerBlocked></Protected>} />
      <Route path="/correction-montants" element={<Protected><OwnerBlocked><FinanceFix /></OwnerBlocked></Protected>} />
      <Route path="/impayes" element={<Protected><OwnerBlocked><Impayes /></OwnerBlocked></Protected>} />
      <Route path="/messagerie" element={<Protected><OwnerBlocked><Inbox /></OwnerBlocked></Protected>} />
      <Route path="/planning" element={<Protected><OwnerBlocked><Planning /></OwnerBlocked></Protected>} />
      <Route path="/degats" element={<Protected><OwnerBlocked><Damages /></OwnerBlocked></Protected>} />
      <Route path="/prospection-proprietaires" element={<Protected><OwnerBlocked><OwnerProspecting /></OwnerBlocked></Protected>} />
      <Route path="/import-donnees" element={<Protected><OwnerBlocked><DataImport /></OwnerBlocked></Protected>} />
      <Route path="/justprice" element={<Protected><OwnerBlocked><JustPrice /></OwnerBlocked></Protected>} />
      <Route path="/estimation/:token" element={<OwnerEstimate />} />
      <Route path="/simulateur/:slug" element={<Simulator />} />
      <Route path="/simulation/:id" element={<SharedSimulation />} />
      <Route path="/avis" element={<Protected><OwnerBlocked><Reviews /></OwnerBlocked></Protected>} />
      <Route path="/equipe" element={<Protected><OwnerBlocked><Team /></OwnerBlocked></Protected>} />
      <Route path="/supplements" element={<Protected><OwnerBlocked><SupplementsPage /></OwnerBlocked></Protected>} />
      <Route path="/mentions-legales" element={<LegalPage slug="mentions-legales" />} />
      <Route path="/cgv" element={<LegalPage slug="cgv" />} />
      <Route path="/confidentialite" element={<LegalPage slug="confidentialite" />} />
      <Route path="/cautions" element={<Protected><OwnerBlocked><CautionsPage /></OwnerBlocked></Protected>} />
      <Route path="/conformite" element={<Protected><OwnerBlocked><Compliance /></OwnerBlocked></Protected>} />
      <Route path="/devis-en-attente" element={<Protected><OwnerBlocked><QuotesPendingPage /></OwnerBlocked></Protected>} />
      <Route path="/guides" element={<Protected><OwnerBlocked><GuidesPage /></OwnerBlocked></Protected>} />
      <Route path="/anciens-logements" element={<Protected><OwnerBlocked><FormerProperties /></OwnerBlocked></Protected>} />
      <Route path="/anciens-logements/:id" element={<Protected><OwnerBlocked><FormerProperties /></OwnerBlocked></Protected>} />
      <Route path="/factures-proprietaires" element={<Protected><OwnerBlocked><OwnerInvoicesPage /></OwnerBlocked></Protected>} />
      <Route path="/templates" element={<Protected><OwnerBlocked><MessageTemplates /></OwnerBlocked></Protected>} />
      <Route path="/parametres" element={<Protected><OwnerBlocked><Settings /></OwnerBlocked></Protected>} />
      <Route path="/carte-visite" element={<ProtectedRaw><OwnerBlocked><BusinessCard /></OwnerBlocked></ProtectedRaw>} />
      <Route path="/aide" element={<Protected><Aide /></Protected>} />
      <Route path="/guide" element={<Protected><OwnerBlocked><StartGuide /></OwnerBlocked></Protected>} />
      <Route path="/audit/overbookings" element={<Protected><OwnerBlocked><AuditOverbookings /></OwnerBlocked></Protected>} />
      <Route path="/tv" element={<ProtectedRaw><OwnerBlocked><TvBoard /></OwnerBlocked></ProtectedRaw>} />
      <Route path="/:slug/*" element={<AgencySite />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <CasaneoAuthProvider>
              <ViewRoleProvider>
                <Toaster position="top-right" richColors closeButton />
                <AppRouter />
              </ViewRoleProvider>
            </CasaneoAuthProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
